import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  AlertTriangle, RefreshCw, CheckCircle2, X, ExternalLink,
  Loader2, Bug, Activity,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const SEVERITY_COLORS = {
  critical: "bg-red-100 text-red-800 border-red-300",
  error: "bg-orange-100 text-orange-800 border-orange-300",
  warning: "bg-amber-100 text-amber-800 border-amber-300",
  info: "bg-blue-100 text-blue-800 border-blue-300",
};

export default function HuntedErrors() {
  const [alerts, setAlerts] = useState([]);
  const [countsBySeverity, setCountsBySeverity] = useState({});
  const [countsByDomain, setCountsByDomain] = useState({});
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [rum, setRum] = useState([]);
  const [showRum, setShowRum] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/hunted-errors?status=${statusFilter}&limit=100`);
      setAlerts(r.data.alerts || []);
      setCountsBySeverity(r.data.counts_by_severity || {});
      setCountsByDomain(r.data.counts_by_domain || {});
    } catch (e) {
      toast.error(`Błąd: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [statusFilter]);

  const loadRum = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/hunted-rum?days=7`);
      setRum(r.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showRum) loadRum(); }, [showRum, loadRum]);

  async function resolve(id, status) {
    try {
      await axios.patch(`${API}/admin/hunted-errors/${id}`, { status });
      toast.success(`Alert ${status}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  // agregacja RUM per domain
  const rumAgg = rum.reduce((acc, r) => {
    if (!acc[r.domain]) acc[r.domain] = {};
    Object.entries(r.metrics || {}).forEach(([m, v]) => {
      if (!acc[r.domain][m]) acc[r.domain][m] = { sum: 0, count: 0 };
      acc[r.domain][m].sum += v.sum || 0;
      acc[r.domain][m].count += v.count || 0;
    });
    return acc;
  }, {});

  return (
    <div className="max-w-none p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bug className="w-6 h-6 text-red-600" />
            Błędy z hunted portali
          </h1>
          <p className="text-slate-600 text-sm">
            Server 500, JS errors, unhandled promises, nginx errors — wszystko z 5 hunted portali w jednym miejscu.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant={showRum ? "default" : "outline"} size="sm"
            onClick={() => setShowRum(v => !v)}
            className={showRum ? "bg-purple-600 hover:bg-purple-700" : ""}>
            <Activity className="w-4 h-4 mr-1" />
            Core Web Vitals (RUM)
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Odśwież
          </Button>
        </div>
      </div>

      {/* Podsumowanie */}
      <div className="grid md:grid-cols-4 gap-3 text-sm">
        <div className="bg-red-50 rounded p-3 border border-red-200">
          <div className="text-xs uppercase text-red-700 font-semibold">Critical</div>
          <div className="text-2xl font-bold text-red-700">{countsBySeverity.critical || 0}</div>
        </div>
        <div className="bg-orange-50 rounded p-3 border border-orange-200">
          <div className="text-xs uppercase text-orange-700 font-semibold">Error</div>
          <div className="text-2xl font-bold text-orange-700">{countsBySeverity.error || 0}</div>
        </div>
        <div className="bg-amber-50 rounded p-3 border border-amber-200">
          <div className="text-xs uppercase text-amber-700 font-semibold">Warning</div>
          <div className="text-2xl font-bold text-amber-700">{countsBySeverity.warning || 0}</div>
        </div>
        <div className="bg-slate-50 rounded p-3 border border-slate-200">
          <div className="text-xs uppercase text-slate-700 font-semibold">Portali z błędami</div>
          <div className="text-2xl font-bold text-slate-700">{Object.keys(countsByDomain).length}</div>
        </div>
      </div>

      {/* Real User Monitoring — Core Web Vitals */}
      {showRum && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-lg">Core Web Vitals — ostatnie 7 dni (real users)</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(rumAgg).length === 0 ? (
              <p className="text-sm text-slate-500">Brak danych — RUM zbiera się po pierwszych odsłonach.</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(rumAgg).map(([dom, metrics]) => (
                  <div key={dom} className="border rounded p-2">
                    <div className="font-mono text-sm font-semibold">{dom}</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                      {Object.entries(metrics).map(([m, v]) => {
                        const avg = v.count > 0 ? (v.sum / v.count) : 0;
                        const unit = m === "cls" ? "" : "ms";
                        const val = m === "cls" ? avg.toFixed(3) : avg.toFixed(0);
                        return (
                          <div key={m} className="bg-slate-50 rounded p-2">
                            <div className="text-slate-500 uppercase text-[10px]">{m}</div>
                            <div className="text-lg font-bold">{val}{unit}</div>
                            <div className="text-[10px] text-slate-500">z {v.count} sesji</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filtry statusu */}
      <div className="flex gap-2 flex-wrap">
        {["open", "acknowledged", "resolved", "all"].map(s => (
          <Button key={s}
            variant={statusFilter === s ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(s)}
            className={statusFilter === s ? "bg-red-600 hover:bg-red-700" : ""}
          >
            {s === "all" ? "Wszystkie" : s === "open" ? "Otwarte" : s === "acknowledged" ? "Ack" : "Rozwiązane"}
          </Button>
        ))}
      </div>

      {loading && alerts.length === 0 && (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
        </div>
      )}

      {alerts.length === 0 && !loading && (
        <Card><CardContent className="py-8 text-center text-emerald-600">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2" />
          Brak błędów w statusie "{statusFilter}" — wszystko OK!
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {alerts.map(a => {
          const ctx = a.context || {};
          return (
            <Card key={a.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Badge className={SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.error}>
                        {a.severity}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {a.error_type.replace("hunted_", "")}
                      </Badge>
                      {a.occurrences > 1 && (
                        <Badge variant="outline" className="bg-red-50 text-red-700">
                          x{a.occurrences}
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-sm mt-1 font-medium">{a.message}</p>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                      {ctx.domain && (
                        <a href={`https://www.${ctx.domain}`} target="_blank" rel="noopener"
                          className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                          {ctx.domain} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <span>· {fmtDate(a.created_at)}</span>
                      {ctx.http_code && <span>· HTTP {ctx.http_code}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {a.status === "open" && (
                      <>
                        <Button size="sm" variant="outline"
                          onClick={() => resolve(a.id, "acknowledged")}>
                          Ack
                        </Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => resolve(a.id, "resolved")}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Rozwiąż
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <button className="text-xs text-slate-500 hover:text-slate-700"
                  onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                  {expanded === a.id ? "Ukryj szczegóły" : "Pokaż szczegóły"}
                </button>
                {expanded === a.id && (
                  <div className="mt-2 space-y-2 text-xs">
                    {ctx.url && (
                      <div><strong>URL:</strong> <a href={ctx.url} target="_blank" rel="noopener" className="text-blue-600 break-all">{ctx.url}</a></div>
                    )}
                    {ctx.user_agent && (
                      <div><strong>User-Agent:</strong> <code className="text-[10px]">{ctx.user_agent}</code></div>
                    )}
                    {ctx.stack && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className="bg-slate-900 text-slate-100 p-2 rounded overflow-x-auto text-[10px] mt-1 max-h-64">{ctx.stack}</pre>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
